import { AxiosInstance } from 'axios'
import { DateTime } from 'luxon'
import qs from 'qs'
import { By } from './By'
import { CheckListField, CheckListFieldForm } from './CheckListsApi'
import { IAreaDto } from './dtos/areaDto'
import Page from './Page'
import { IUserDto } from './dtos/userDto'
import { Search as S, Infer } from '@app/lib/hooks/useSearch'

export type Status = 'completed' | 'in_progress' | 'todo'

export declare namespace FacilityCheck {
  export interface Details {
    facilityCheck: FacilityCheck
    fields: CheckListField[]
  }
  export interface Params {
    facility?: string[]
    summary?: string[]
    deviations?: boolean
    status?: Status[]
  }
  export interface Form {
    facility: string
    time: DateTime
    fields: CheckListFieldForm[]
    summary: string
    status: Status
    assignee?: string
  }
}

export interface Completion {
  status: Status
  assignee?: IUserDto
}

export interface FacilityCheck {
  id: string
  facility: IAreaDto
  summary: string
  modified: By
  negative: number
  time: string
  completion: Completion
}

export const ParamsSchema = {
  ...Page.schema,
  facility: S.array(S.string),
  summary: S.array(S.string),
  deviations: S.undefine(S.boolean),
  status: S.array(S.string),
}
export type Params = Infer<typeof ParamsSchema>

export class FacilityCheckApi {
  private readonly http: AxiosInstance

  constructor(http: AxiosInstance) {
    this.http = http
  }

  async list(tenantRef: string, search: FacilityCheck.Params, pagination: Page.Search): Promise<Page<FacilityCheck>> {
    const params = { ...search, ...pagination }
    const response = await this.http.get(`/ports/${tenantRef}/facilityChecks`, {
      params,
      paramsSerializer: (p) => qs.stringify(p, { arrayFormat: 'repeat' }),
    })
    return response.data
  }

  async get(tenantRef: string, id: string): Promise<FacilityCheck.Details> {
    const response = await this.http.get(`/ports/${tenantRef}/facilityChecks/${id}`)
    return response.data
  }

  async create(tenantRef: string, form: FacilityCheck.Form): Promise<string> {
    const postData = { ...form, time: form.time.toUTC() }
    const response = await this.http.post(`/ports/${tenantRef}/facilityChecks`, postData)
    return response.data.id
  }

  async update(tenantRef: string, id: string, form: FacilityCheck.Form): Promise<void> {
    const postData = { ...form, time: form.time.toUTC() }
    await this.http.put(`/ports/${tenantRef}/facilityChecks/${id}`, postData)
  }

  async delete(tenantRef: string, id: string): Promise<void> {
    await this.http.delete(`/ports/${tenantRef}/facilityChecks/${id}`)
  }
}
